import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { pushSupported, getPushStatus, enablePush, disablePush } from '../../lib/push';
import NotificationBell from '../NotificationBell';
import {
    LayoutDashboard,
    Users,
    TrendingUp,
    Calendar,
    LogOut,
    Building2,
    UserCog,
    Map as MapIcon,
    BarChart3,
    ClipboardList,
    FileText,
    PackageCheck,
    DatabaseBackup,
    Bell,
    BellOff,
    X,
    NotebookPen
} from 'lucide-react';

const Sidebar = ({ mobileOpen = false, onClose = () => {} }) => {
    const location = useLocation();
    const { user, logout, isAdmin, isBackoffice, isAddettoConsegna } = useAuth();

    const [pushStatus, setPushStatus] = useState('default');
    const [pushBusy, setPushBusy] = useState(false);

    useEffect(() => {
        if (!pushSupported()) { setPushStatus('unsupported'); return; }
        getPushStatus().then(setPushStatus).catch(() => setPushStatus('default'));
    }, []);

    const handleTogglePush = async () => {
        if (pushBusy) return;
        setPushBusy(true);
        try {
            if (pushStatus === 'subscribed') {
                await disablePush();
                setPushStatus('default');
                toast.success('Notifiche disattivate');
            } else {
                await enablePush();
                setPushStatus('subscribed');
                toast.success('Notifiche attivate su questo dispositivo');
            }
        } catch (e) {
            if (e.message === 'denied') {
                toast.error('Permesso negato. Abilita le notifiche dalle impostazioni del browser.');
                setPushStatus('denied');
            } else if (e.message === 'unsupported') {
                toast.error('Notifiche non supportate su questo dispositivo/browser.');
            } else {
                toast.error('Impossibile attivare le notifiche. Su iPhone installa prima l\'app sulla Home.');
            }
        } finally {
            setPushBusy(false);
        }
    };

    const allNavItems = [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/clients', label: 'Clienti', icon: Building2 },
        { path: '/commesse', label: 'Assegnazione\nTelai - Commesse', icon: ClipboardList },
        { path: '/preventivi', label: 'Preventivi', icon: FileText },
        { path: '/pipeline', label: 'Commesse', icon: TrendingUp },
        { path: '/consegnati', label: 'Consegnati', icon: PackageCheck },
        { path: '/calendar', label: 'Calendario', icon: Calendar },
        { path: '/relazioni', label: 'Relazioni', icon: NotebookPen },
        { path: '/map', label: 'Mappa', icon: MapIcon },
        { path: '/reports', label: 'Report', icon: BarChart3 },
    ];

    let navItems = allNavItems;
    if (isAddettoConsegna()) {
        // Addetto alla consegna: solo il calendario
        navItems = allNavItems.filter((i) => i.path === '/calendar');
    } else if (isBackoffice()) {
        // Backoffice: niente Dashboard, Report, Mappa
        navItems = allNavItems.filter((i) => !['/dashboard', '/reports', '/map'].includes(i.path));
    }

    const adminItems = [
        { path: '/users', label: 'Utenti', icon: UserCog },
        { path: '/territori', label: 'Territori', icon: MapIcon },
        { path: '/backup', label: 'Backup', icon: DatabaseBackup },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <aside
            className={`sidebar fixed left-0 top-0 h-screen w-64 flex flex-col z-50 transform transition-transform duration-200 ease-in-out ${
                mobileOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0`}
            data-testid="sidebar"
        >
            {/* Logo + close on mobile */}
            <div
                className="p-6 border-b border-white/10 flex items-start justify-between"
                style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top))' }}
            >
                <div>
                    <img
                        src="/pezzaioli-logo.png"
                        alt="Pezzaioli"
                        className="h-10 w-auto"
                        data-testid="sidebar-logo"
                    />
                    <p className="text-xs text-white/60 uppercase tracking-wider mt-2">
                        CRM Commerciale
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="md:hidden p-2 -mr-1 text-white/70 hover:text-white active:bg-white/10 rounded"
                    aria-label="Chiudi menu"
                    data-testid="mobile-menu-close"
                >
                    <X size={24} />
                </button>
                <div className="hidden md:block -mr-1">
                    <NotificationBell variant="dark" />
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {navItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                        data-testid={`nav-${item.path.slice(1)}`}
                    >
                        <item.icon size={20} />
                        <span className="whitespace-pre-line leading-tight">{item.label}</span>
                    </Link>
                ))}

                {isAdmin() && (
                    <>
                        <div className="pt-4 pb-2">
                            <p className="text-xs text-white/40 uppercase tracking-wider px-3">
                                Amministrazione
                            </p>
                        </div>
                        {adminItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                                data-testid={`nav-${item.path.slice(1)}`}
                            >
                                <item.icon size={20} />
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </>
                )}
            </nav>

            {/* User section */}
            <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center text-white font-heading">
                        {user?.name?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                            {user?.name}
                        </p>
                        <p className="text-xs text-white/60 truncate">
                            {{ admin: 'Amministratore', area_manager: 'Area Manager', backoffice: 'Backoffice', addetto_consegna: 'Addetto alla Consegna' }[user?.role] || 'Commerciale'}
                        </p>
                    </div>
                </div>
                {pushStatus !== 'unsupported' && (
                    <button
                        onClick={handleTogglePush}
                        disabled={pushBusy || pushStatus === 'denied'}
                        className="sidebar-link w-full text-white/70 hover:text-white mb-1 disabled:opacity-50"
                        data-testid="toggle-push-btn"
                        aria-label="Attiva o disattiva notifiche"
                    >
                        {pushStatus === 'subscribed' ? <Bell size={20} /> : <BellOff size={20} />}
                        <span>
                            {pushStatus === 'subscribed'
                                ? 'Notifiche attive'
                                : pushStatus === 'denied'
                                    ? 'Notifiche bloccate'
                                    : 'Attiva notifiche'}
                        </span>
                    </button>
                )}
                <button
                    onClick={logout}
                    className="sidebar-link w-full text-white/70 hover:text-white"
                    data-testid="logout-btn"
                    aria-label="Esci dal sistema"
                >
                    <LogOut size={20} />
                    <span>Esci</span>
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;
